import { Attr, BelongsTo, HasMany, Model } from "spraypaint";

import { ApplicationRecord } from "shared/services/spraypaint";

import type ContactList from "./ContactList";
import type ContactListsContact from "./ContactListsContact";
import type Influencer from "./Influencer";
import type InfluencersOutlet from "./InfluencersOutlet";
import type OutletAddress from "./OutletAddress";
import type OutletDesk from "./OutletDesk";
import type OutletType from "./OutletType";
import type OutreachTopic from "./OutreachTopic";
import type { Email, Homepage, InternalId, Phone, Social } from "./types";

type TargetArea = {
  targetArea: string;
  targetAreaId: number;
};

type Rate = {
  currencyId: number;
  currencyISO: string;
  currencyName: string;
  rate: number;
  rateType: string;
};

export type Circulation = {
  circulation: string;
  circulationStartDate: string;
  circulationType: string;
};

type Coverage = {
  coverage: string;
  coverageId: number;
};

type Frequency = {
  frequency: string;
  frequencyId: number;
};

@Model()
class Outlet extends ApplicationRecord {
  static jsonapiType = "outlets";

  @Attr() adEqvTier: number;

  @Attr() archivedAt: string;

  @Attr() avatarUrl?: string;

  @Attr() circulation: string;

  @Attr() circulations: Circulation[];

  @HasMany() contactLists: ContactList[];

  @HasMany() contactListsContacts: ContactListsContact[];

  @Attr() contentTypes: string[];

  @Attr() coverage: string;

  @Attr() coverages: Coverage[];

  @Attr({ persist: false }) dmaNames: string[];

  @Attr({ persist: false }) documentId: string;

  @Attr() emails: Email[];

  @Attr() faxes: Phone[];

  @Attr() freelanceOutletFlag: boolean;

  @Attr() frequencies: Frequency[];

  @Attr({ persist: false }) gpodId: string;

  @Attr() group: string;

  @Attr() highResAvatarUrl: string;

  @Attr() homepages: Homepage[];

  @HasMany() influencers: Influencer[];

  @Attr() influencersCount: number;

  @HasMany() influencersOutlets: InfluencersOutlet[];

  @Attr() internalIds: InternalId[];

  @Attr() invalidEmail: boolean;

  @Attr() languages: string[];

  @Attr() location: string;

  @Attr() monitoringOnlyFlag: boolean;

  @Attr() name: string;

  @Attr() normalizedCirculations: Circulation[];

  @HasMany({ name: "outletAddresses" })
  outletAddresses: OutletAddress[];

  @HasMany({ name: "outletDesks" }) outletDesks: OutletDesk[];

  @HasMany() outletOutreachTopics: OutreachTopic[];

  @BelongsTo() outletType: OutletType;

  @Attr() owner: string;

  @BelongsTo() parentOutlet: Outlet;

  @Attr() phones: Phone[];

  @Attr() profile: string;

  @Attr() rates: Rate[];

  @Attr() socials: Social[];

  @Attr() status: string;

  @Attr() targetAreas: TargetArea[];

  @Attr() uniqueVisitorsPerMonth: number;

  @Attr() verifiedBy: string;

  @Attr() verifiedDate: string;

  get email(): string | undefined {
    return this.emails?.find((email) => email.defaultFlag)?.email;
  }
}

export default Outlet;
